import { AsyncPipe,NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatRipple } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { NgxPermissionsModule } from 'ngx-permissions';
import { Subscription } from 'rxjs';

import { MenuService } from '@core';
import { FeatureCheckDirective } from '@shared/directives/feature-check.directive';
import { MinRoleDirective } from '@shared/directives/min-role.directive';

import { isCustomIcon, TIconItem } from '@shared/utils/isCustomIcon';

import { NavAccordionDirective } from './nav-accordion.directive';
import { NavAccordionItemDirective } from './nav-accordion-item.directive';
import { NavAccordionToggleDirective } from './nav-accordion-toggle.directive';

@Component({
    selector: 'app-sidemenu',
    templateUrl: './sidemenu.component.html',
    styleUrls: ['./sidemenu.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
      NgTemplateOutlet,
      NavAccordionDirective,
      NgxPermissionsModule,
      NavAccordionItemDirective,
      MatRipple,
      RouterLink,
      NavAccordionToggleDirective,
      MatIcon,
      AsyncPipe,
      TranslatePipe,
      FeatureCheckDirective,
      MinRoleDirective,
    ]
})
export class SidemenuComponent implements OnDestroy {
  // Note: Ripple effect make page flashing on mobile
  @Input() ripple = false;
  @Input() toggleChecked = false;

  public menu$ = this.menuService.getAll();
  public buildRoute = this.menuService.buildRoute;

  private routerSubscription = Subscription.EMPTY;
  private currentUrl = this.normalizeUrl(this.router.url);
  private lastConfirmedUrl = this.normalizeUrl(this.router.url);

  constructor(
    private menuService: MenuService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Highlight immediately on click — before any SLR cleanup can interfere
        // console.log('[Sidemenu] NavigationStart:', event.url);
        this.currentUrl = this.normalizeUrl(event.url);
        this.cdr.markForCheck();
      } else if (event instanceof NavigationEnd) {
        // Confirm with final URL (handles internal redirects, e.g. /dossier → /dossier/overview/projects)
        // console.log('[Sidemenu] NavigationEnd:', event.url, '->', event.urlAfterRedirects);
        this.lastConfirmedUrl = this.normalizeUrl(event.urlAfterRedirects);
        this.currentUrl = this.lastConfirmedUrl;
        this.cdr.markForCheck();
      } else if (event instanceof NavigationCancel) {
        // Revert to last known good URL if navigation was blocked
        // console.log('[Sidemenu] NavigationCancel:', event.url, '| reason:', event.reason);
        this.currentUrl = this.lastConfirmedUrl;
        this.cdr.markForCheck();
      } else if (event instanceof NavigationError) {
        // console.log('[Sidemenu] NavigationError:', event.url);
        this.currentUrl = this.lastConfirmedUrl;
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
  }

  public isLinkActive(route: string, parentRoute: string[] = []): boolean {
    if (route === '/dashboard') {
      return this.currentUrl === '/dashboard';
    }

    const linkPath = this.buildRoute(parentRoute.concat([route]));
    return this.currentUrl === linkPath || this.currentUrl.startsWith(`${linkPath}/`);
  }

  public isCustomIcon(item: TIconItem): boolean {
    return isCustomIcon(item);
  }

  public refreshDashboardPage(event?: Event): void {
    event?.preventDefault();

    // Already on dashboard: full reload to clear conversation state.
    // Elsewhere: single router navigation (avoid routerLink + navigate double-fire).
    if (this.normalizeUrl(this.router.url) === '/dashboard') {
      localStorage.setItem('bot_sources_global', JSON.stringify([]));
      window.location.reload();
      return;
    }

    this.router.navigateByUrl('/dashboard');
  }

  public refreshDossierPage(event?: Event): void {
    event?.preventDefault();

    const target = '/dossier/overview/projects';
    const current = this.normalizeUrl(this.router.url);

    // Already under dossier: force a remount. Same-URL clicks are ignored by the
    // router config, which leaves a collapsed blank shell stuck as "loaded".
    if (current === '/dossier' || current.startsWith('/dossier/')) {
      void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        void this.router.navigateByUrl(target);
      });
      return;
    }

    void this.router.navigateByUrl(target);
  }

  public isDossierMenuRoute(route: string): boolean {
    return route === 'dossier' || route === '/dossier';
  }

  private normalizeUrl(url: string): string {
    return url.split('?')[0].split('#')[0];
  }
}
